import type { DashboardTicket } from './TicketRow';

import { css, html } from 'react-strict-dom';
import { Api } from 'src/api';
import { Text } from 'src/components';
import { color } from 'src/constants/color.css';
import { Model } from 'src/models';

import TakeItAction from './TakeItAction';
import TicketsSection from './TicketsSection';

type Props = {
  /** Limits the list to work this user could pick up, e.g. /users/{id}. */
  userIri?: string;
  /** When set, offers to put this user on a skill they are able to do. */
  taker?: Api.User.V_Self;
};

function NeededSkills({ ticket }: { ticket: DashboardTicket }) {
  const needed = Model.TicketSkill.filterUnassigned(ticket.skills);
  if (needed.length === 0) return null;

  const names = needed.map((skill) =>
    Model.TicketSkill.getTypeName(skill.type)
  );

  return (
    <html.span style={styles.neededBadge}>
      <Text typeface="body.small.regular" color="neutral80">
        {`${names.join(', ')} needed`}
      </Text>
    </html.span>
  );
}

export default function AvailableTicketsSection({ userIri, taker }: Props) {
  const api = Api.Ticket.useReadCollectionAvailable({ user: userIri });

  return (
    <TicketsSection
      title="Available Tickets"
      api={api}
      emptyMessage="No tickets are waiting to be picked up"
      renderAction={(ticket) =>
        taker && (
          <TakeItAction
            skills={ticket.skills}
            user={taker}
            onTaken={() => api.invalidate()}
          />
        )
      }
      renderFooter={(ticket) => <NeededSkills ticket={ticket} />}
    />
  );
}

const styles = css.create({
  neededBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: color.neutral10,
    paddingInline: 8,
    paddingBlock: 4,
  },
});
